import { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../Moduels/Context/AuthContext";
import { sendRegistrationEmailOtp, verifyRegistrationEmailOtp } from "../../Moduels/auth/auth";
import "./auth.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SEC = 60;
const OTP_EXPIRY_SEC = 600;

/* ── Password rule definitions ── */
const PWD_RULES = [
  { key: "len",  label: "8+ characters",    test: (p) => p.length >= 8 },
  { key: "up",   label: "Uppercase letter",  test: (p) => /[A-Z]/.test(p) },
  { key: "lo",   label: "Lowercase letter",  test: (p) => /[a-z]/.test(p) },
  { key: "num",  label: "Number",            test: (p) => /[0-9]/.test(p) },
  { key: "sym",  label: "Special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function getStrength(pwd) {
  if (!pwd) return { score: 0, label: "", color: "" };
  const n = PWD_RULES.filter((r) => r.test(pwd)).length;
  const levels = [
    { label: "",        color: "" },
    { label: "Weak",    color: "#ef4444" },
    { label: "Weak",    color: "#ef4444" },
    { label: "Fair",    color: "#f59e0b" },
    { label: "Good",    color: "#2563EB" },
    { label: "Strong",  color: "#10b981" },
  ];
  return { score: n, ...levels[n] };
}

const FEATURES = [
  "Automated Payroll Processing",
  "Attendance & Leave Management",
  "Shift & Roster Scheduling",
  "Compliance & Tax Automation",
  "Employee Self-Service Portal",
];

/* ── Shared icon components ── */
const CheckIco = ({ size = 12, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3.5 5-5.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CircleIco = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const EyeOpen = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <path d="M2 8s2.5-5 6-5 6 5 6 5-2.5 5-6 5-6-5-6-5z" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const EyeOff = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <path d="M2 8s2.5-5 6-5 6 5 6 5-2.5 5-6 5-6-5-6-5z" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3 3l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/* ── Main component ── */
export default function RegisterModal({ onClose, onSwitchToLogin, fullPage = true }) {
  const { register } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = (searchParams.get("plan") || "basic").toLowerCase();

  const [form, setForm] = useState({
    fullName: "", email: "", orgName: "", orgCode: "",
    username: "", password: "", confirm: "",
  });
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [showPwd, setShowPwd]   = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [agreed, setAgreed]     = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpFeedback, setOtpFeedback] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [otpExpirySeconds, setOtpExpirySeconds] = useState(0);
  const overlayRef = useRef(null);

  const goToLogin = useCallback(() => {
    if (onSwitchToLogin) onSwitchToLogin();
    else if (onClose) onClose();
    else navigate("/login");
  }, [onSwitchToLogin, onClose, navigate]);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
    else goToLogin();
  }, [onClose, goToLogin]);

  /* Redirect to login after success */
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(goToLogin, 4000);
    return () => clearTimeout(t);
  }, [success, goToLogin]);

  /* Resend countdown */
  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const t = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendSeconds]);

  /* OTP expiry countdown */
  useEffect(() => {
    if (otpExpirySeconds <= 0) return undefined;
    const t = setTimeout(() => setOtpExpirySeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpExpirySeconds]);

  /* Escape key + body scroll lock */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showOtpModal) setShowOtpModal(false);
      else handleClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [handleClose, showOtpModal]);

  const handleOverlay = (e) => { if (e.target === overlayRef.current) onClose(); };

  const field = (key) => (e) => {
    setForm((p) => ({ ...p, [key]: e.target.value }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const handleEmailChange = (e) => {
    const nextEmail = e.target.value;
    setForm((p) => ({ ...p, email: nextEmail }));
    setErrors((p) => ({ ...p, email: "" }));
    if (emailVerified && nextEmail.trim().toLowerCase() !== verifiedEmail.toLowerCase()) {
      setEmailVerified(false);
      setVerifiedEmail("");
      setVerificationToken("");
    }
  };

  const formatCountdown = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const sendOtp = async () => {
    const email = form.email.trim();
    if (!email) {
      setErrors((p) => ({ ...p, email: "Required" }));
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setErrors((p) => ({ ...p, email: "Invalid email" }));
      return;
    }

    setSendingOtp(true);
    setOtpFeedback(null);
    try {
      const data = await sendRegistrationEmailOtp(email);
      setOtp("");
      setShowOtpModal(true);
      setResendSeconds(RESEND_COOLDOWN_SEC);
      setOtpExpirySeconds(OTP_EXPIRY_SEC);
      const baseMsg = "Verification code sent to your email.";
      setOtpFeedback({
        type: "success",
        text: data?.dev_otp ? `${baseMsg} Dev OTP: ${data.dev_otp}` : baseMsg,
      });
    } catch (err) {
      const msg = err?.message || "Failed to send verification code.";
      setErrors((p) => ({ ...p, email: msg }));
      setOtpFeedback({ type: "error", text: msg });
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const code = otp.trim();
    if (!code || !/^\d{6}$/.test(code)) {
      setOtpFeedback({ type: "error", text: "Enter a valid 6-digit code." });
      return;
    }
    if (otpExpirySeconds <= 0) {
      setOtpFeedback({ type: "error", text: "OTP has expired. Please request a new code." });
      return;
    }

    setVerifyingOtp(true);
    setOtpFeedback(null);
    try {
      const data = await verifyRegistrationEmailOtp(form.email.trim(), code);
      const token = data?.email_verification_token;
      setEmailVerified(true);
      setVerifiedEmail(form.email.trim());
      setVerificationToken(token || "");
      setShowOtpModal(false);
      setOtp("");
      setOtpFeedback(null);
    } catch (err) {
      setOtpFeedback({ type: "error", text: err?.message || "Invalid verification code." });
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || sendingOtp) return;
    // sendOtp sets success/error feedback (do not force success here)
    await sendOtp();
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim())  e.fullName = "Required";
    if (!form.email.trim())     e.email = "Required";
    else if (!EMAIL_RE.test(form.email)) e.email = "Invalid email";
    else if (!emailVerified)    e.email = "Please verify your email address";
    if (!form.orgName.trim())   e.orgName = "Required";
    if (!form.username.trim())  e.username = "Required";
    else if (form.username.length < 3) e.username = "Min. 3 chars";
    if (!form.password)         e.password = "Required";
    else if (form.password.length < 8) e.password = "Min. 8 characters";
    if (!form.confirm)          e.confirm = "Required";
    else if (form.password !== form.confirm) e.confirm = "Passwords do not match";
    if (!agreed)                e.terms = "Please accept the terms to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await register({
        username:                  form.username,
        email:                     form.email,
        password:                  form.password,
        full_name:                 form.fullName,
        organisation_name:         form.orgName,
        organisation_code:         form.orgCode,
        email_verification_token:  verificationToken,
        plan_code:                 selectedPlan,
        billing_cycle:             "monthly",
      });
      setSuccess(true);
    } catch (err) {
      setErrors({ global: err?.message || "Registration failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const strength    = getStrength(form.password);
  const rules       = PWD_RULES.map((r) => ({ ...r, met: form.password ? r.test(form.password) : null }));
  const pwdMatch    = form.confirm !== "" && form.password === form.confirm;
  const pwdMismatch = form.confirm !== "" && form.password !== form.confirm;

  return (
    <div
      className={`modal-overlay reg-root${fullPage ? " reg-fullpage-overlay" : ""}`}
      ref={overlayRef}
      onClick={fullPage ? undefined : handleOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Create your account"
    >
      <div className={`reg-wrap${fullPage ? " reg-wrap-fullpage" : ""}`}>

        {/* ══════════════════════════════════════════
            SUCCESS STATE
        ══════════════════════════════════════════ */}
        {success ? (
          <div className="reg-suc-screen">
            <div className="reg-suc-icon">
              <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
                <circle cx="26" cy="26" r="25" stroke="currentColor" strokeWidth="2" />
                <path d="M16 26l8 8 12-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="reg-suc-title">Workspace Created Successfully</h2>
            <div className="reg-suc-card">
              <div className="reg-suc-row">
                <span className="reg-suc-key">Organization</span>
                <span className="reg-suc-val">{form.orgName}</span>
              </div>
              <div className="reg-suc-row">
                <span className="reg-suc-key">Admin Email</span>
                <span className="reg-suc-val">{form.email}</span>
              </div>
            </div>
            <p className="reg-suc-note">Redirecting you to sign in…</p>
            <div className="reg-redirect-track">
              <div className="reg-redirect-fill" style={{ animationDuration: "4s" }} />
            </div>
            <button className="auth-btn primary-btn" style={{ marginTop: "1.5rem", maxWidth: 200 }} onClick={goToLogin}>
              Sign In Now
            </button>
          </div>
        ) : (
          <>
            {/* ══════════════════════════════════════
                STICKY HEADER
            ══════════════════════════════════════ */}
            <div className={`reg-hdr${fullPage ? " reg-hdr-fullpage" : ""}`}>
              <span className="reg-hdr-title">Create Your Workspace</span>
              <span className="reg-plan-pill">{selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} · 3 months free</span>
              <button className="reg-hdr-close" onClick={handleClose} aria-label="Close">
                <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
                  <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* ══════════════════════════════════════
                BODY (left panel + right form)
            ══════════════════════════════════════ */}
            <div className="reg-body">

              {/* Left branding panel */}
              <div className="reg-left" aria-hidden="true">
                <div className="reg-left-inner">
                  <p className="reg-eyebrow">Payroll &amp; HRMS Platform</p>
                  <h3 className="reg-headline">
                    Manage Your<br />Workforce<br />Effortlessly
                  </h3>
                  <p className="reg-tagline">
                    Trusted by so many companies
                  </p>

                  <ul className="reg-features">
                    {FEATURES.map((f) => (
                      <li key={f} className="reg-feat">
                        <span className="reg-feat-dot">
                          <CheckIco size={10} color="#10B981" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="reg-badges">
                    <span className="reg-badge">SOC2</span>
                    <span className="reg-badge">GDPR</span>
                    <span className="reg-badge">India TDS</span>
                  </div>
                </div>

                {/* Decorative circles */}
                <div className="reg-decor reg-decor-1" />
                <div className="reg-decor reg-decor-2" />
              </div>

              {/* Right form panel */}
              <div className="reg-right">
                <form id="reg-form" onSubmit={handleSubmit} noValidate>

                  {errors.global && (
                    <div className="auth-error reg-global-err" role="alert">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M8 5v3M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      {errors.global}
                    </div>
                  )}

                  {/* ── Row 1: Full Name + Email ── */}
                  <div className="reg-row">
                    <div className="reg-field">
                      <label className="reg-lbl" htmlFor="r-name">
                        Full Name <span className="reg-star">*</span>
                      </label>
                      <div className="field-wrapper">
                        <span className="field-icon">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M2 14c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input id="r-name" type="text" autoFocus autoComplete="name"
                          className={`field-input ri ${errors.fullName ? "input-error" : ""}`}
                          placeholder="John Doe"
                          value={form.fullName} onChange={field("fullName")} />
                      </div>
                      {errors.fullName && <p className="reg-err">{errors.fullName}</p>}
                    </div>

                    <div className="reg-field reg-field-email">
                      <label className="reg-lbl" htmlFor="r-email">
                        Email Address <span className="reg-star">*</span>
                        {emailVerified && (
                          <span className="reg-email-verified">
                            <CheckIco size={10} color="#fff" /> Verified
                          </span>
                        )}
                      </label>
                      <div className="reg-email-row">
                        <div className="field-wrapper reg-email-input-wrap">
                          <span className="field-icon">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M1 5l7 5 7-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </span>
                          <input id="r-email" type="email" autoComplete="email"
                            className={`field-input ri ${errors.email ? "input-error" : emailVerified ? "input-success" : ""}`}
                            placeholder="john@company.com"
                            value={form.email}
                            onChange={handleEmailChange}
                            disabled={emailVerified}
                            readOnly={emailVerified} />
                        </div>
                        {!emailVerified && (
                          <button
                            type="button"
                            className={`reg-verify-btn ${sendingOtp ? "loading" : ""}`}
                            onClick={sendOtp}
                            disabled={sendingOtp}
                          >
                            {sendingOtp ? (
                              <><span className="spinner reg-verify-spinner" /> Sending…</>
                            ) : (
                              "Verify Email"
                            )}
                          </button>
                        )}
                      </div>
                      {errors.email && <p className="reg-err">{errors.email}</p>}
                    </div>
                  </div>

                  {/* ── Row 2: Org Name + Org Code ── */}
                  <div className="reg-row">
                    <div className="reg-field">
                      <label className="reg-lbl" htmlFor="r-org">
                        Organization Name <span className="reg-star">*</span>
                      </label>
                      <div className="field-wrapper">
                        <span className="field-icon">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <rect x="2" y="4" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M6 8h4M6 11h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input id="r-org" type="text" autoComplete="organization"
                          className={`field-input ri ${errors.orgName ? "input-error" : ""}`}
                          placeholder="Acme Pvt Ltd"
                          value={form.orgName} onChange={field("orgName")} />
                      </div>
                      {errors.orgName && <p className="reg-err">{errors.orgName}</p>}
                    </div>

                    <div className="reg-field">
                      <label className="reg-lbl" htmlFor="r-code">
                        Org Code <span className="reg-opt">Optional</span>
                      </label>
                      <div className="field-wrapper">
                        <span className="field-icon">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M5 8h.5M7.5 8h1M11 8h.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input id="r-code" type="text"
                          className="field-input ri"
                          placeholder="ACM001"
                          value={form.orgCode} onChange={field("orgCode")} />
                      </div>
                    </div>
                  </div>

                  {/* ── Row 3: Username + Password ── */}
                  <div className="reg-row">
                    <div className="reg-field">
                      <label className="reg-lbl" htmlFor="r-user">
                        Username <span className="reg-star">*</span>
                      </label>
                      <div className="field-wrapper">
                        <span className="field-icon">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M8 2C4.686 2 2 4.686 2 8s2.686 6 6 6 6-2.686 6-6-2.686-6-6-6z" stroke="currentColor" strokeWidth="1.5" />
                            <circle cx="5.8" cy="7.5" r="0.7" fill="currentColor" />
                            <circle cx="10.2" cy="7.5" r="0.7" fill="currentColor" />
                            <path d="M5.5 10.2s.5.8 2.5.8 2.5-.8 2.5-.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input id="r-user" type="text" autoComplete="username"
                          className={`field-input ri ${errors.username ? "input-error" : ""}`}
                          placeholder="johndoe"
                          value={form.username} onChange={field("username")} />
                      </div>
                      {errors.username && <p className="reg-err">{errors.username}</p>}
                    </div>

                    <div className="reg-field">
                      <label className="reg-lbl" htmlFor="r-pwd">
                        Password <span className="reg-star">*</span>
                      </label>
                      <div className="field-wrapper">
                        <span className="field-icon">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input id="r-pwd" autoComplete="new-password"
                          type={showPwd ? "text" : "password"}
                          className={`field-input ri ${errors.password ? "input-error" : ""}`}
                          placeholder="Min. 8 characters"
                          value={form.password} onChange={field("password")} />
                        <button type="button" className="toggle-password"
                          onClick={() => setShowPwd((v) => !v)}
                          aria-label={showPwd ? "Hide" : "Show"}>
                          {showPwd ? <EyeOff /> : <EyeOpen />}
                        </button>
                      </div>
                      {form.password && (
                        <div className="password-strength ri-strength">
                          <div className="strength-bars">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <div key={i}
                                className={`strength-bar ${strength.score >= i ? "filled" : ""}`}
                                style={strength.score >= i ? { backgroundColor: strength.color } : {}} />
                            ))}
                          </div>
                          {strength.label && (
                            <span className="strength-label" style={{ color: strength.color }}>
                              {strength.label}
                            </span>
                          )}
                        </div>
                      )}
                      {errors.password && <p className="reg-err">{errors.password}</p>}
                    </div>
                  </div>

                  {/* ── Row 4: Confirm Password + Requirements ── */}
                  <div className="reg-row reg-row-top">
                    <div className="reg-field">
                      <label className="reg-lbl" htmlFor="r-conf">
                        Confirm Password <span className="reg-star">*</span>
                      </label>
                      <div className="field-wrapper">
                        <span className="field-icon" style={pwdMatch ? { color: "#10b981" } : {}}>
                          {pwdMatch ? (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                              <rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                              <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          )}
                        </span>
                        <input id="r-conf" autoComplete="new-password"
                          type={showConf ? "text" : "password"}
                          className={`field-input ri ${
                            errors.confirm || pwdMismatch ? "input-error"
                            : pwdMatch ? "input-success" : ""
                          }`}
                          placeholder="Re-enter password"
                          value={form.confirm} onChange={field("confirm")} />
                        <button type="button" className="toggle-password"
                          onClick={() => setShowConf((v) => !v)}
                          aria-label={showConf ? "Hide" : "Show"}>
                          {showConf ? <EyeOff /> : <EyeOpen />}
                        </button>
                      </div>
                      {pwdMatch && !errors.confirm && (
                        <p className="reg-match">
                          <CheckIco size={11} color="#10b981" /> Passwords match
                        </p>
                      )}
                      {errors.confirm && <p className="reg-err">{errors.confirm}</p>}
                    </div>

                    {/* Password requirements panel */}
                    <div className="reg-field">
                      <p className="reg-lbl">Requirements</p>
                      <div className="reg-rules">
                        {rules.map((r) => (
                          <div key={r.key}
                            className={`reg-rule ${r.met === true ? "rule-ok" : r.met === false ? "rule-no" : ""}`}>
                            <span className="reg-rule-ico">
                              {r.met ? <CheckIco size={10} /> : <CircleIco size={10} />}
                            </span>
                            {r.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                </form>
              </div>
            </div>

            {/* ══════════════════════════════════════
                STICKY FOOTER
            ══════════════════════════════════════ */}
            <div className="reg-ftr">
              <div className="reg-terms-wrap">
                <input type="checkbox" id="reg-agree" className="reg-ckb"
                  checked={agreed}
                  onChange={(e) => {
                    setAgreed(e.target.checked);
                    setErrors((p) => ({ ...p, terms: "" }));
                  }} />
                <label htmlFor="reg-agree" className="reg-terms-lbl">
                  <strong className="reg-terms-strong">
                    I agree to the{" "}
                    <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="reg-lnk">Terms</a>
                    {" "}&amp;{" "}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="reg-lnk">Privacy Policy</a>
                  </strong>
                </label>
                {errors.terms && <span className="reg-terms-err">{errors.terms}</span>}
              </div>

              <div className="reg-cta-wrap">
                <button form="reg-form" type="submit"
                  className={`auth-btn primary-btn reg-cta-btn ${loading ? "loading" : ""}`}
                  disabled={loading || !emailVerified}>
                  {loading ? (
                    <><span className="spinner" /> Creating…</>
                  ) : (
                    <>
                      Create Workspace
                      <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  )}
                </button>
                {!emailVerified && (
                  <p className="reg-cta-help">Verify your email to enable Create Workspace.</p>
                )}
              </div>
            </div>
          </>
        )}

      </div>

      {showOtpModal && (
        <div
          className="modal-overlay reg-otp-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Verify email"
          onClick={(e) => { if (e.target === e.currentTarget) setShowOtpModal(false); }}
        >
          <div className="modal-card modal-card--sm reg-otp-card">
            <button
              type="button"
              className="modal-close"
              onClick={() => setShowOtpModal(false)}
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>

            <div className="modal-icon-header">
              <div className="modal-icon-circle">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M3 7l9 7 9-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            <div className="auth-header">
              <h2 className="auth-title">Verify your email</h2>
              <p className="auth-subtitle">
                Enter the 6-digit code sent to <strong>{form.email.trim()}</strong>
              </p>
            </div>

            {otpFeedback && (
              <div
                className={otpFeedback.type === "success" ? "auth-success reg-otp-msg" : "auth-error reg-otp-msg"}
                role="alert"
              >
                {otpFeedback.text}
              </div>
            )}

            <div className="reg-field" style={{ marginTop: "0.75rem" }}>
              <label className="reg-lbl" htmlFor="r-otp">Verification code</label>
              <div className="field-wrapper">
                <input
                  id="r-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="field-input ri reg-otp-input"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </div>
              <p className="reg-otp-timer">
                {otpExpirySeconds > 0
                  ? `Code expires in ${formatCountdown(otpExpirySeconds)}`
                  : "Code expired. Resend to get a new one."}
              </p>
            </div>

            <button
              type="button"
              className={`auth-btn primary-btn ${verifyingOtp ? "loading" : ""}`}
              onClick={handleVerifyOtp}
              disabled={verifyingOtp || otpExpirySeconds <= 0}
              style={{ marginTop: "0.75rem" }}
            >
              {verifyingOtp ? (
                <><span className="spinner" /> Verifying…</>
              ) : (
                "Verify OTP"
              )}
            </button>

            <div className="reg-otp-resend">
              <button
                type="button"
                className="reg-otp-resend-btn"
                onClick={handleResendOtp}
                disabled={resendSeconds > 0 || sendingOtp}
              >
                Resend OTP
              </button>
              {resendSeconds > 0 && (
                <span className="reg-otp-resend-timer">Resend in {resendSeconds}s</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
